/**
 * abilitySfxSpecs — Cast/hit procedural tones (fighters, mages, supports, SPY-411 ADCs).
 * Inspired-by LoL kit feel; original oscillator params (no Riot assets).
 */

export const SPY111_FIGHTER_SFX_KEYS = ['darius', 'sett', 'jax'];
export const SPY211_MAGE_SFX_KEYS = ['lux', 'brand', 'veigar'];
export const SPY311_SUPPORT_SFX_KEYS = ['soraka', 'lulu', 'nami'];
export const SPY411_ADC_SFX_KEYS = ['ezreal', 'caitlyn'];
/** Loop 6 residual — lux/brand only (veigar stays on SPY-211). */
export const SPY511_MAGE_SFX_KEYS = ['lux', 'brand'];
export const SPY611_ASSASSIN_SFX_KEYS = ['zed', 'katarina'];
/** Loop 11 assassin residual — zed/katarina SFX lock (SPY-1011). */
export const SPY1011_ASSASSIN_SFX_KEYS = ['zed', 'katarina'];
/** Loop 12 support residual — soraka/lulu SFX lock (SPY-1111). */
export const SPY1111_SUPPORT_SFX_KEYS = ['soraka', 'lulu'];
/** Loop 13 mage residual — lux/brand SFX lock (SPY-1211). */
export const SPY1211_MAGE_SFX_KEYS = ['lux', 'brand'];
export const SPY711_TANK_SFX_KEYS = ['garen', 'sett', 'darius'];
export const SPY811_SKIRMISHER_SFX_KEYS = ['yasuo', 'riven', 'irelia'];
/** Loop 10 marksman residual — ashe/sivir SFX gap fill (SPY-911). */
export const SPY911_MARKSMAN_SFX_KEYS = ['ashe', 'sivir'];
/** FID-AUD-01 — core mage residual: annie/ryze cast/hit gap fill. */
export const FID_AUD_01_SFX_KEYS = ['annie', 'ryze'];

const SLOTS = ['q', 'w', 'e', 'r'];

function idsForChampKeys(champKeys) {
  const ids = [];
  for (const key of champKeys) {
    for (const slot of SLOTS) ids.push(`${key}_${slot}`);
  }
  return ids;
}

/** @type {Record<string, { cast: object, hit: object }>} */
export const ABILITY_SFX_SPECS = {
  garen_q: {
    cast: { freqHz: 155, durationMs: 95, wave: 'sawtooth', gain: 0.11 },
    hit: { freqHz: 82, durationMs: 150, wave: 'square', gain: 0.13 }
  },
  garen_w: {
    cast: { freqHz: 130, durationMs: 110, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 75, durationMs: 140, wave: 'sine', gain: 0.11 }
  },
  garen_e: {
    cast: { freqHz: 180, durationMs: 85, wave: 'sawtooth', gain: 0.12 },
    hit: { freqHz: 90, durationMs: 130, wave: 'square', gain: 0.13 }
  },
  garen_r: {
    cast: { freqHz: 100, durationMs: 125, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 58, durationMs: 210, wave: 'square', gain: 0.15 }
  },
  darius_q: {
    cast: { freqHz: 165, durationMs: 90, wave: 'sawtooth', gain: 0.12 },
    hit: { freqHz: 88, durationMs: 140, wave: 'square', gain: 0.14 }
  },
  darius_w: {
    cast: { freqHz: 210, durationMs: 70, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 120, durationMs: 110, wave: 'sawtooth', gain: 0.12 }
  },
  darius_e: {
    cast: { freqHz: 140, durationMs: 100, wave: 'sawtooth', gain: 0.11 },
    hit: { freqHz: 70, durationMs: 160, wave: 'square', gain: 0.13 }
  },
  darius_r: {
    cast: { freqHz: 95, durationMs: 130, wave: 'sawtooth', gain: 0.15 },
    hit: { freqHz: 55, durationMs: 220, wave: 'square', gain: 0.16 }
  },
  sett_q: {
    cast: { freqHz: 190, durationMs: 80, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 105, durationMs: 120, wave: 'sawtooth', gain: 0.12 }
  },
  sett_w: {
    cast: { freqHz: 230, durationMs: 95, wave: 'sawtooth', gain: 0.12 },
    hit: { freqHz: 80, durationMs: 180, wave: 'square', gain: 0.15 }
  },
  sett_e: {
    cast: { freqHz: 175, durationMs: 85, wave: 'square', gain: 0.11 },
    hit: { freqHz: 95, durationMs: 130, wave: 'sawtooth', gain: 0.13 }
  },
  sett_r: {
    cast: { freqHz: 110, durationMs: 140, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 60, durationMs: 240, wave: 'square', gain: 0.16 }
  },
  jax_q: {
    cast: { freqHz: 260, durationMs: 75, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 150, durationMs: 100, wave: 'sawtooth', gain: 0.12 }
  },
  jax_w: {
    cast: { freqHz: 300, durationMs: 65, wave: 'sine', gain: 0.09 },
    hit: { freqHz: 180, durationMs: 90, wave: 'triangle', gain: 0.11 }
  },
  jax_e: {
    cast: { freqHz: 240, durationMs: 90, wave: 'square', gain: 0.1 },
    hit: { freqHz: 130, durationMs: 150, wave: 'sawtooth', gain: 0.13 }
  },
  jax_r: {
    cast: { freqHz: 200, durationMs: 120, wave: 'sawtooth', gain: 0.13 },
    hit: { freqHz: 100, durationMs: 200, wave: 'square', gain: 0.14 }
  },
  lux_q: {
    cast: { freqHz: 520, durationMs: 85, wave: 'sine', gain: 0.11 },
    hit: { freqHz: 380, durationMs: 120, wave: 'triangle', gain: 0.12 }
  },
  lux_w: {
    cast: { freqHz: 610, durationMs: 70, wave: 'triangle', gain: 0.09 },
    hit: { freqHz: 440, durationMs: 100, wave: 'sine', gain: 0.1 }
  },
  lux_e: {
    cast: { freqHz: 480, durationMs: 95, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 320, durationMs: 140, wave: 'triangle', gain: 0.12 }
  },
  lux_r: {
    cast: { freqHz: 700, durationMs: 130, wave: 'sawtooth', gain: 0.13 },
    hit: { freqHz: 240, durationMs: 200, wave: 'square', gain: 0.14 }
  },
  brand_q: {
    cast: { freqHz: 340, durationMs: 80, wave: 'sawtooth', gain: 0.11 },
    hit: { freqHz: 190, durationMs: 130, wave: 'square', gain: 0.13 }
  },
  brand_w: {
    cast: { freqHz: 280, durationMs: 100, wave: 'sawtooth', gain: 0.12 },
    hit: { freqHz: 150, durationMs: 170, wave: 'square', gain: 0.14 }
  },
  brand_e: {
    cast: { freqHz: 360, durationMs: 75, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 210, durationMs: 120, wave: 'sawtooth', gain: 0.12 }
  },
  brand_r: {
    cast: { freqHz: 250, durationMs: 120, wave: 'sawtooth', gain: 0.13 },
    hit: { freqHz: 110, durationMs: 210, wave: 'square', gain: 0.16 }
  },
  veigar_q: {
    cast: { freqHz: 420, durationMs: 80, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 260, durationMs: 110, wave: 'sine', gain: 0.11 }
  },
  veigar_w: {
    cast: { freqHz: 300, durationMs: 110, wave: 'sawtooth', gain: 0.11 },
    hit: { freqHz: 160, durationMs: 160, wave: 'square', gain: 0.13 }
  },
  veigar_e: {
    cast: { freqHz: 390, durationMs: 100, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 220, durationMs: 150, wave: 'triangle', gain: 0.12 }
  },
  veigar_r: {
    cast: { freqHz: 180, durationMs: 140, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 90, durationMs: 230, wave: 'square', gain: 0.16 }
  },
  soraka_q: {
    cast: { freqHz: 640, durationMs: 80, wave: 'sine', gain: 0.12 },
    hit: { freqHz: 420, durationMs: 110, wave: 'triangle', gain: 0.12 }
  },
  soraka_w: {
    cast: { freqHz: 580, durationMs: 70, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 360, durationMs: 130, wave: 'triangle', gain: 0.12 }
  },
  soraka_e: {
    cast: { freqHz: 490, durationMs: 95, wave: 'triangle', gain: 0.11 },
    hit: { freqHz: 280, durationMs: 150, wave: 'sine', gain: 0.13 }
  },
  soraka_r: {
    cast: { freqHz: 720, durationMs: 140, wave: 'sine', gain: 0.14 },
    hit: { freqHz: 240, durationMs: 220, wave: 'triangle', gain: 0.15 }
  },
  lulu_q: {
    cast: { freqHz: 560, durationMs: 75, wave: 'triangle', gain: 0.11 },
    hit: { freqHz: 340, durationMs: 105, wave: 'sine', gain: 0.12 }
  },
  lulu_w: {
    cast: { freqHz: 680, durationMs: 85, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 410, durationMs: 120, wave: 'triangle', gain: 0.11 }
  },
  lulu_e: {
    cast: { freqHz: 610, durationMs: 70, wave: 'triangle', gain: 0.11 },
    hit: { freqHz: 330, durationMs: 115, wave: 'sine', gain: 0.12 }
  },
  lulu_r: {
    cast: { freqHz: 220, durationMs: 130, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 110, durationMs: 210, wave: 'square', gain: 0.16 }
  },
  nami_q: {
    cast: { freqHz: 430, durationMs: 90, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 210, durationMs: 140, wave: 'triangle', gain: 0.12 }
  },
  nami_w: {
    cast: { freqHz: 470, durationMs: 80, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 260, durationMs: 125, wave: 'sine', gain: 0.11 }
  },
  nami_e: {
    cast: { freqHz: 510, durationMs: 75, wave: 'sine', gain: 0.09 },
    hit: { freqHz: 300, durationMs: 110, wave: 'triangle', gain: 0.1 }
  },
  nami_r: {
    cast: { freqHz: 280, durationMs: 150, wave: 'sawtooth', gain: 0.13 },
    hit: { freqHz: 140, durationMs: 230, wave: 'square', gain: 0.15 }
  },
  ezreal_q: {
    cast: { freqHz: 540, durationMs: 75, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 320, durationMs: 105, wave: 'triangle', gain: 0.11 }
  },
  ezreal_w: {
    cast: { freqHz: 620, durationMs: 80, wave: 'triangle', gain: 0.09 },
    hit: { freqHz: 380, durationMs: 120, wave: 'sine', gain: 0.1 }
  },
  ezreal_e: {
    cast: { freqHz: 480, durationMs: 70, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 260, durationMs: 110, wave: 'triangle', gain: 0.11 }
  },
  ezreal_r: {
    cast: { freqHz: 360, durationMs: 140, wave: 'sawtooth', gain: 0.13 },
    hit: { freqHz: 160, durationMs: 220, wave: 'square', gain: 0.15 }
  },
  caitlyn_q: {
    cast: { freqHz: 290, durationMs: 95, wave: 'sawtooth', gain: 0.11 },
    hit: { freqHz: 150, durationMs: 150, wave: 'square', gain: 0.13 }
  },
  caitlyn_w: {
    cast: { freqHz: 340, durationMs: 85, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 180, durationMs: 130, wave: 'sawtooth', gain: 0.12 }
  },
  caitlyn_e: {
    cast: { freqHz: 410, durationMs: 80, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 220, durationMs: 120, wave: 'triangle', gain: 0.11 }
  },
  caitlyn_r: {
    cast: { freqHz: 220, durationMs: 150, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 95, durationMs: 240, wave: 'square', gain: 0.16 }
  },
  zed_q: {
    cast: { freqHz: 390, durationMs: 70, wave: 'sawtooth', gain: 0.12 },
    hit: { freqHz: 185, durationMs: 120, wave: 'square', gain: 0.14 }
  },
  zed_w: {
    cast: { freqHz: 270, durationMs: 85, wave: 'triangle', gain: 0.11 },
    hit: { freqHz: 135, durationMs: 140, wave: 'sawtooth', gain: 0.13 }
  },
  zed_e: {
    cast: { freqHz: 330, durationMs: 75, wave: 'square', gain: 0.12 },
    hit: { freqHz: 165, durationMs: 130, wave: 'sawtooth', gain: 0.14 }
  },
  zed_r: {
    cast: { freqHz: 190, durationMs: 140, wave: 'sawtooth', gain: 0.15 },
    hit: { freqHz: 85, durationMs: 230, wave: 'square', gain: 0.17 }
  },
  katarina_q: {
    cast: { freqHz: 430, durationMs: 70, wave: 'triangle', gain: 0.11 },
    hit: { freqHz: 250, durationMs: 110, wave: 'sine', gain: 0.13 }
  },
  katarina_w: {
    cast: { freqHz: 490, durationMs: 65, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 290, durationMs: 100, wave: 'triangle', gain: 0.12 }
  },
  katarina_e: {
    cast: { freqHz: 530, durationMs: 60, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 310, durationMs: 95, wave: 'triangle', gain: 0.11 }
  },
  katarina_r: {
    cast: { freqHz: 250, durationMs: 130, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 125, durationMs: 210, wave: 'square', gain: 0.16 }
  },
  yasuo_q: {
    cast: { freqHz: 350, durationMs: 75, wave: 'sawtooth', gain: 0.11 },
    hit: { freqHz: 170, durationMs: 115, wave: 'triangle', gain: 0.12 }
  },
  yasuo_w: {
    cast: { freqHz: 280, durationMs: 95, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 140, durationMs: 145, wave: 'triangle', gain: 0.11 }
  },
  yasuo_e: {
    cast: { freqHz: 400, durationMs: 60, wave: 'sine', gain: 0.09 },
    hit: { freqHz: 210, durationMs: 100, wave: 'triangle', gain: 0.1 }
  },
  yasuo_r: {
    cast: { freqHz: 200, durationMs: 140, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 90, durationMs: 220, wave: 'square', gain: 0.16 }
  },
  riven_q: {
    cast: { freqHz: 310, durationMs: 70, wave: 'square', gain: 0.11 },
    hit: { freqHz: 155, durationMs: 120, wave: 'sawtooth', gain: 0.13 }
  },
  riven_w: {
    cast: { freqHz: 270, durationMs: 80, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 135, durationMs: 130, wave: 'square', gain: 0.12 }
  },
  riven_e: {
    cast: { freqHz: 330, durationMs: 85, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 165, durationMs: 125, wave: 'triangle', gain: 0.11 }
  },
  riven_r: {
    cast: { freqHz: 190, durationMs: 145, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 85, durationMs: 230, wave: 'square', gain: 0.16 }
  },
  irelia_q: {
    cast: { freqHz: 440, durationMs: 65, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 250, durationMs: 105, wave: 'triangle', gain: 0.11 }
  },
  irelia_w: {
    cast: { freqHz: 390, durationMs: 90, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 200, durationMs: 135, wave: 'sine', gain: 0.11 }
  },
  irelia_e: {
    cast: { freqHz: 460, durationMs: 70, wave: 'sawtooth', gain: 0.11 },
    hit: { freqHz: 230, durationMs: 115, wave: 'triangle', gain: 0.12 }
  },
  irelia_r: {
    cast: { freqHz: 230, durationMs: 135, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 110, durationMs: 215, wave: 'square', gain: 0.15 }
  },
  ashe_q: {
    cast: { freqHz: 510, durationMs: 70, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 300, durationMs: 110, wave: 'triangle', gain: 0.11 }
  },
  ashe_w: {
    cast: { freqHz: 560, durationMs: 80, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 330, durationMs: 120, wave: 'sine', gain: 0.11 }
  },
  ashe_e: {
    cast: { freqHz: 480, durationMs: 90, wave: 'sine', gain: 0.09 },
    hit: { freqHz: 260, durationMs: 140, wave: 'triangle', gain: 0.1 }
  },
  ashe_r: {
    cast: { freqHz: 220, durationMs: 140, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 100, durationMs: 220, wave: 'square', gain: 0.15 }
  },
  sivir_q: {
    cast: { freqHz: 370, durationMs: 75, wave: 'sawtooth', gain: 0.11 },
    hit: { freqHz: 190, durationMs: 125, wave: 'triangle', gain: 0.12 }
  },
  sivir_w: {
    cast: { freqHz: 420, durationMs: 70, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 210, durationMs: 115, wave: 'sine', gain: 0.11 }
  },
  sivir_e: {
    cast: { freqHz: 450, durationMs: 65, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 240, durationMs: 105, wave: 'triangle', gain: 0.11 }
  },
  sivir_r: {
    cast: { freqHz: 280, durationMs: 130, wave: 'sawtooth', gain: 0.13 },
    hit: { freqHz: 130, durationMs: 200, wave: 'square', gain: 0.14 }
  },
  annie_q: {
    cast: { freqHz: 355, durationMs: 80, wave: 'sawtooth', gain: 0.11 },
    hit: { freqHz: 195, durationMs: 125, wave: 'square', gain: 0.13 }
  },
  annie_w: {
    cast: { freqHz: 310, durationMs: 95, wave: 'sawtooth', gain: 0.12 },
    hit: { freqHz: 165, durationMs: 155, wave: 'square', gain: 0.14 }
  },
  annie_e: {
    cast: { freqHz: 405, durationMs: 75, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 230, durationMs: 115, wave: 'sine', gain: 0.11 }
  },
  annie_r: {
    cast: { freqHz: 235, durationMs: 135, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 105, durationMs: 225, wave: 'square', gain: 0.16 }
  },
  ryze_q: {
    cast: { freqHz: 495, durationMs: 75, wave: 'triangle', gain: 0.11 },
    hit: { freqHz: 285, durationMs: 115, wave: 'sine', gain: 0.12 }
  },
  ryze_w: {
    cast: { freqHz: 445, durationMs: 90, wave: 'sine', gain: 0.1 },
    hit: { freqHz: 255, durationMs: 140, wave: 'triangle', gain: 0.12 }
  },
  ryze_e: {
    cast: { freqHz: 535, durationMs: 70, wave: 'triangle', gain: 0.1 },
    hit: { freqHz: 305, durationMs: 120, wave: 'sine', gain: 0.11 }
  },
  ryze_r: {
    cast: { freqHz: 265, durationMs: 145, wave: 'sawtooth', gain: 0.14 },
    hit: { freqHz: 120, durationMs: 230, wave: 'square', gain: 0.15 }
  }
};

export function getAbilitySfxSpec(abilityId) {
  return ABILITY_SFX_SPECS[abilityId] || null;
}

export function listFighterAbilitySfxIds() {
  return idsForChampKeys(SPY111_FIGHTER_SFX_KEYS);
}

export function listMageAbilitySfxIds() {
  return idsForChampKeys(SPY211_MAGE_SFX_KEYS);
}

export function listSupportAbilitySfxIds() {
  return idsForChampKeys(SPY311_SUPPORT_SFX_KEYS);
}

export function listAdcAbilitySfxIds() {
  return idsForChampKeys(SPY411_ADC_SFX_KEYS);
}

export function listSpy511MageAbilitySfxIds() {
  return idsForChampKeys(SPY511_MAGE_SFX_KEYS);
}

export function listAssassinAbilitySfxIds() {
  return idsForChampKeys(SPY611_ASSASSIN_SFX_KEYS);
}

export function listSpy1011AssassinAbilitySfxIds() {
  return idsForChampKeys(SPY1011_ASSASSIN_SFX_KEYS);
}

export function listSpy1111SupportAbilitySfxIds() {
  return idsForChampKeys(SPY1111_SUPPORT_SFX_KEYS);
}

export function listSpy1211MageAbilitySfxIds() {
  return idsForChampKeys(SPY1211_MAGE_SFX_KEYS);
}

export function listTankAbilitySfxIds() {
  return idsForChampKeys(SPY711_TANK_SFX_KEYS);
}

export function listSkirmisherAbilitySfxIds() {
  return idsForChampKeys(SPY811_SKIRMISHER_SFX_KEYS);
}

export function listMarksmanAbilitySfxIds() {
  return idsForChampKeys(SPY911_MARKSMAN_SFX_KEYS);
}

export function listFidAud01AbilitySfxIds() {
  return idsForChampKeys(FID_AUD_01_SFX_KEYS);
}
